import type { OrderStatus, UserRole, NotificationChannel, NotificationStatus } from './enums';

// ===== User =====
export interface User {
  id: number;
  email: string | null;
  full_name: string;
  role: UserRole;
  branch_id: number | null;
  phone: string | null;
  must_change_password: boolean;
  is_active: boolean;
  is_pending: boolean;
  atomi_id: number | null;
  created_at: string;
}

export interface LoginCredentials {
  identifier: string;
  password: string;
}

// ===== Branch =====
export interface Branch {
  id: number;
  name: string;
  address: string | null;
  created_at: string;
}

// ===== Order =====
export interface Order {
  id: number;
  branch_id: number;
  order_number: string;
  consolidated_delivery_id: number | null;
  customer_id: number | null;
  invoice_number: string | null;
  status: OrderStatus;
  qr_code_token: string | null;
  qr_code_expires_at: string | null;
  shelf_location: string | null;
  received_at: string | null;
  handed_off_at: string | null;
  notes: string | null;
  handoff_notes: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_code: string | null;
  created_at: string;
  updated_at: string;
}

// ===== Order Item (detail) =====
export interface OrderItemDetail {
  id: number;
  sku: string;
  name: string;
  quantity_ordered: number;
  quantity_received: number;
  quantity_handed_off: number;
  price_per_unit: number | null;
  vat_amount: number | null;
  total_price: number | null;
}

// ===== Order Detail =====
export interface OrderDetail extends Order {
  customer_email?: string | null;
  items: OrderItemDetail[];
}

// ===== Order Item (legacy) =====
export interface OrderItem {
  id: number;
  order_id: number;
  name: string;
  sku: string | null;
  quantity: number;
  received_quantity: number | null;
}

// ===== Order Filters =====
export interface OrderFilters {
  search?: string;
  status?: OrderStatus;
  page?: number;
  limit?: number;
}

// ===== Import / Delivery =====
export interface ImportRecord {
  import_id: number;
  group_id: string;
  status: string;
  orders_count: number;
  created_at: string;
  import_batch_id: number | null;
}

export interface ImportOrderItem {
  id: number;
  sku: string;
  name: string;
  quantity_ordered: number;
  quantity_received: number;
  quantity_handed_off: number;
  price_per_unit: number | null;
  vat_amount: number | null;
  total_price: number | null;
}

export interface ImportOrder {
  id: number;
  order_number: string;
  invoice_number: string | null;
  customer_name: string | null;
  status: string;
  items: ImportOrderItem[];
}

export interface ImportDetail extends ImportRecord {
  orders: ImportOrder[];
}

export interface ImportBatch {
  batch_id: number;
  status: string;
  deliveries: ImportRecord[];
  created_at: string;
}

export interface ImportBatchDetail {
  batch_id: number;
  status: string;
  deliveries: ImportDetail[];
  created_at: string;
}

// ===== Warehouse / Shortages =====
export const ShortageType = {
  MISSING: 'MISSING',
  DAMAGED: 'DAMAGED',
  WRONG_ITEM: 'WRONG_ITEM',
} as const;

export type ShortageType = (typeof ShortageType)[keyof typeof ShortageType];

export const ShortageTypeLabels: Record<ShortageType, string> = {
  MISSING: 'Недостача',
  DAMAGED: 'Повреждение',
  WRONG_ITEM: 'Неверный товар',
};

export interface Shortage {
  id: number;
  order_id: number;
  order_item_id: number | null;
  consolidated_delivery_id: number | null;
  reported_by_id: number;
  shortage_type: ShortageType;
  description: string | null;
  resolved: boolean;
  created_at: string;
}

export interface AcceptDeliveryResponse {
  delivery_id: number;
  delivery_status: string;
  orders: Array<{
    order_id: number;
    order_number: string;
    status: OrderStatus;
  }>;
  stock_movements_created: number;
}

// ===== Inventory =====
export const InventoryCheckStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
} as const;

export type InventoryCheckStatus = (typeof InventoryCheckStatus)[keyof typeof InventoryCheckStatus];

export interface StockLevel {
  product_id: number;
  sku: string;
  name: string;
  quantity: number;
}

export const MovementType = {
  INBOUND: 'INBOUND',
  OUTBOUND_PICKUP: 'OUTBOUND_PICKUP',
  ADJUSTMENT: 'ADJUSTMENT',
} as const;

export type MovementType = (typeof MovementType)[keyof typeof MovementType];

export const MovementTypeLabels: Record<MovementType, string> = {
  INBOUND: 'Приход',
  OUTBOUND_PICKUP: 'Выдача',
  ADJUSTMENT: 'Корректировка',
};

export interface StockMovement {
  id: number;
  branch_id: number;
  product_id: number;
  order_id: number | null;
  movement_type: MovementType;
  quantity: number;
  quantity_before: number;
  quantity_after: number;
  performed_by_id: number;
  reference: string | null;
  created_at: string;
}

export interface InventoryCheckItem {
  id: number;
  product_id: number;
  expected_quantity: number;
  actual_quantity: number | null;
  discrepancy: number | null;
  notes: string | null;
}

export interface InventoryCheck {
  id: number;
  branch_id: number;
  performed_by_id: number;
  status: InventoryCheckStatus;
  started_at: string;
  completed_at: string | null;
  notes: string | null;
  items: InventoryCheckItem[];
}

export interface DiscrepancyReportItem {
  product_id: number;
  expected_quantity: number;
  actual_quantity: number;
  discrepancy: number;
  notes: string | null;
}

export interface DiscrepancyReport {
  check_id: number;
  branch_id: number;
  status: InventoryCheckStatus;
  started_at: string;
  completed_at: string | null;
  total_items: number;
  items_with_discrepancies: number;
  items: DiscrepancyReportItem[];
}

// ===== Notification =====
export interface Notification {
  id: number;
  user_id: number;
  order_id: number | null;
  channel: NotificationChannel;
  status: NotificationStatus;
  message_template: string | null;
  message_body: string | null;
  sent_at: string | null;
  error_message: string | null;
  is_read: boolean;
  created_at: string;
}

// ===== Delivery Sheets =====
export interface DeliverySheetItem {
  id: number;
  delivery_sheet_id: number;
  order_id: number;
  customer_name: string;
  shelf_location: string | null;
  status: string;
}

export interface DeliverySheet {
  id: number;
  branch_id: number;
  consolidated_delivery_id: number;
  generated_at: string;
  generated_by_id: number;
  items: DeliverySheetItem[];
}
